import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors,
} from "@dnd-kit/core";
import {
    SortableContext, rectSortingStrategy, sortableKeyboardCoordinates, useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useAuth } from "../../context/AuthContext";
import { useAlertContext } from "../../context/AlertContext";
import { DEPARTMENTS } from "../../config/api";
import { getDashboard } from "../../services/analyticsApi";
import { getBoardPrefs, getExecutiveOverview, saveBoardPrefs } from "../../services/executiveApi";
import {
    DEFAULT_PREFS, KPI_METRICS, MODULE_GROUPS, PERIODS, SPANS, SPAN_CLASS, WIDGETS, WIDGET_BY_ID, addModule,
    availableTypes, defaultOptions, mergeLayout, removeModule, reorderLayout, setWidgetOption, toggleInList, updateWidget,
} from "../../executive/boardLayout";
import { WIDGET_COMPONENTS, linkOptionsForRole } from "./ExecutiveWidgets";

/**
 * Главная страница из модулей (правки №41–42). Каждый сам добавляет модули из каталога, расставляет их
 * (перетаскиванием или стрелками), задаёт ширину и настройки; один модуль можно добавить несколько раз.
 * Модуль привязан к вкладке: без доступа к вкладке модуль не показывается и не предлагается в каталоге.
 * Раскладка личная, хранится на сервере. [Claude] 2026-11-03
 */

const selectCls = "border border-slate-200 rounded px-2 py-1 text-sm max-w-full";

function OptionControl({ opt, value, onChange, ctx }) {
    const select = (values) => (
        <label className="flex items-center justify-between gap-2 text-sm">
            <span className="text-slate-600 shrink-0">{opt.label}</span>
            <select
                className={selectCls}
                value={String(value ?? "")}
                onChange={(e) => {
                    const raw = values.find(([v]) => String(v) === e.target.value);
                    onChange(raw ? raw[0] : e.target.value);
                }}
            >
                {values.map(([v, l]) => <option key={String(v)} value={String(v)}>{l}</option>)}
            </select>
        </label>
    );
    const chips = (values) => {
        const list = Array.isArray(value) ? value : [];
        return (
            <div>
                <div className="text-xs text-slate-500 mb-1">{opt.label}</div>
                <div className="flex flex-wrap gap-1">
                    {values.map(([v, l]) => (
                        <button
                            key={v}
                            type="button"
                            onClick={() => onChange(toggleInList(list, v))}
                            className={`px-2 py-0.5 rounded border text-xs ${list.includes(v) ? "bg-[#e0bb48] border-[#e0bb48] text-black" : "bg-white border-slate-200 text-slate-600"}`}
                        >
                            {l}
                        </button>
                    ))}
                </div>
            </div>
        );
    };
    switch (opt.type) {
        case "bool":
            return (
                <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={value !== false} onChange={(e) => onChange(e.target.checked)} />
                    {opt.label}
                </label>
            );
        case "multi":
            return chips(opt.values);
        case "links":
            return chips(linkOptionsForRole(ctx.role));
        case "department":
            return select([["", "Все отделы"], ...DEPARTMENTS.map((d) => [d, d])]);
        case "metric": {
            const forbidden = new Set(ctx.data?.meta?.forbidden || []);
            return select(KPI_METRICS.filter((m) => !forbidden.has(m.block)).map((m) => [m.key, m.label]));
        }
        case "vacancy": {
            const list = ctx.data?.pipeline?.vacancies || [];
            return select([["", "— выберите —"], ...list.map((v) => [String(v.id), `${v.name}${v.department ? ` · ${v.department}` : ""}`])]);
        }
        case "text":
            return (
                <label className="sm:col-span-2 text-sm">
                    <span className="text-xs text-slate-500">{opt.label}</span>
                    <textarea rows={4} maxLength={2000} className="mt-0.5 w-full border border-slate-200 rounded px-2 py-1 text-sm" value={value || ""} onChange={(e) => onChange(e.target.value)} />
                </label>
            );
        case "text_short":
            return (
                <label className="flex items-center justify-between gap-2 text-sm">
                    <span className="text-slate-600 shrink-0">{opt.label}</span>
                    <input type="text" maxLength={60} className={selectCls} value={value || ""} onChange={(e) => onChange(e.target.value)} />
                </label>
            );
        default:
            return select(opt.values || []);
    }
}

function ModuleCard({ widget, def, editing, ctx, onPatch, onOption, onMove, onRemove, isFirst, isLast, children }) {
    const [open, setOpen] = useState(false);
    const [help, setHelp] = useState(false);
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: widget.id, disabled: !editing });
    const style = { transform: CSS.Translate.toString(transform), transition, zIndex: isDragging ? 20 : undefined, opacity: isDragging ? 0.85 : undefined };
    return (
        <section ref={setNodeRef} style={style} data-widget={widget.type} data-module-id={widget.id}
            className={`col-span-1 ${SPAN_CLASS[widget.span] || SPAN_CLASS[2]} bg-white rounded-xl border ${editing ? "border-dashed border-[#e0bb48]" : "border-slate-200/70"} shadow-sm flex flex-col min-w-0`}>
            <header className="flex items-center gap-1 px-3 h-9 border-b border-slate-100">
                {editing && (
                    <button type="button" title="Перетащите, чтобы переставить модуль" aria-label={`Переставить: ${def.title}`} className="cursor-grab active:cursor-grabbing px-1 text-slate-400 hover:text-slate-800 touch-none" {...attributes} {...listeners}>⠿</button>
                )}
                <h2 className="font-semibold text-sm text-slate-800 truncate">{def.title}</h2>
                <button type="button" title="Что показывает модуль" onClick={() => setHelp((v) => !v)} className="w-5 h-5 rounded-full text-[11px] text-slate-400 hover:text-slate-700 hover:bg-slate-100">i</button>
                <div className="ml-auto flex items-center gap-0.5">
                    {editing && (
                        <>
                            <button type="button" title="Раньше" disabled={isFirst} onClick={() => onMove(-1)} className="px-1.5 text-slate-500 disabled:opacity-30 hover:text-slate-900">↑</button>
                            <button type="button" title="Позже" disabled={isLast} onClick={() => onMove(1)} className="px-1.5 text-slate-500 disabled:opacity-30 hover:text-slate-900">↓</button>
                            <select title="Ширина" value={widget.span} onChange={(e) => onPatch({ span: Number(e.target.value) })} className="text-xs border border-slate-200 rounded px-1 py-0.5">
                                {SPANS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                            </select>
                            <button type="button" title="Убрать модуль с главной" aria-label={`Убрать: ${def.title}`} onClick={onRemove} className="px-1.5 text-slate-400 hover:text-red-600">✕</button>
                        </>
                    )}
                    {def.options?.length ? (
                        <button type="button" title="Настроить модуль" aria-label={`Настроить: ${def.title}`} onClick={() => setOpen((v) => !v)} className={`px-1.5 rounded ${open ? "bg-slate-100 text-slate-900" : "text-slate-400 hover:text-slate-800"}`}>⚙</button>
                    ) : null}
                </div>
            </header>
            {help && <div className="px-3 py-1.5 text-xs text-slate-600 bg-slate-50 border-b border-slate-100">{def.help}</div>}
            {open && (
                <div className="px-3 py-2 bg-amber-50/40 border-b border-slate-100 grid gap-2 sm:grid-cols-2">
                    {def.options.map((opt) => (
                        <OptionControl key={opt.key} opt={opt} ctx={ctx} value={widget.options[opt.key]} onChange={(v) => onOption(opt.key, v)} />
                    ))}
                    <button type="button" className="text-xs text-slate-500 hover:text-slate-800 justify-self-start" onClick={() => onPatch({ options: defaultOptions(def) })}>Сбросить настройки модуля</button>
                </div>
            )}
            <div className="p-3 flex-1 min-w-0">{children}</div>
        </section>
    );
}

function ModuleCatalog({ available, layout, onAdd, onClose }) {
    const count = layout.reduce((acc, w) => { acc[w.type] = (acc[w.type] || 0) + 1; return acc; }, {});
    return (
        <div className="bg-white border border-[#e0bb48] rounded-xl shadow-sm p-3" role="dialog" aria-label="Каталог модулей">
            <div className="flex items-center mb-2">
                <span className="font-semibold text-slate-800">Добавить модуль</span>
                <span className="ml-2 text-xs text-slate-500">модуль появится первым — дальше переставьте мышкой; один модуль можно добавить несколько раз с разными настройками</span>
                <button type="button" onClick={onClose} className="ml-auto px-2 text-slate-400 hover:text-slate-800" aria-label="Закрыть каталог">✕</button>
            </div>
            <div className="space-y-3">
                {MODULE_GROUPS.map((group) => {
                    const items = WIDGETS.filter((d) => d.group === group && available.has(d.id));
                    if (!items.length) return null;
                    return (
                        <div key={group}>
                            <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">{group}</div>
                            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                                {items.map((d) => (
                                    <div key={d.id} className="border border-slate-200 rounded-lg px-3 py-2 flex flex-col gap-1">
                                        <div className="flex items-center gap-2">
                                            <span className="font-medium text-sm text-slate-900">{d.title}</span>
                                            {count[d.id] ? <span className="text-[11px] text-slate-400">на главной: {count[d.id]}</span> : null}
                                        </div>
                                        <div className="text-xs text-slate-500 leading-snug flex-1">{d.help}</div>
                                        <button type="button" onClick={() => onAdd(d.id)} aria-label={`Добавить модуль ${d.title}`} className="self-start px-2 py-0.5 rounded bg-[#e0bb48] hover:bg-[#d4af3a] text-black text-xs font-medium">+ Добавить</button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export default function ExecutiveBoard() {
    const { showAlert } = useAlertContext();
    const { user } = useAuth() || {};
    const [prefs, setPrefs] = useState(DEFAULT_PREFS);
    const [layout, setLayout] = useState(() => mergeLayout([], false));
    const [prefsReady, setPrefsReady] = useState(false);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tasks, setTasks] = useState([]);
    const [tasksLoading, setTasksLoading] = useState(true);
    const [editing, setEditing] = useState(false);
    const [catalog, setCatalog] = useState(false);
    const saveTimer = useRef(null);

    useEffect(() => {
        let cancelled = false;
        getBoardPrefs()
            .then((p) => {
                if (cancelled || !p) return;
                setPrefs({ ...DEFAULT_PREFS, ...p });
                setLayout(mergeLayout(p.widgets, Boolean(p.configured)));
            })
            .catch(() => {})
            .finally(() => { if (!cancelled) setPrefsReady(true); });
        getDashboard()
            .then((res) => { if (!cancelled) setTasks(Array.isArray(res?.tasks) ? res.tasks : []); })
            .catch(() => {})
            .finally(() => { if (!cancelled) setTasksLoading(false); });
        return () => { cancelled = true; };
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getExecutiveOverview({
                periodDays: prefs.period_days,
                department: prefs.department || "",
                staleDays: prefs.stale_days,
            });
            setData(res);
        } catch (e) {
            showAlert(`Не удалось загрузить показатели: ${e.message || e}`, "error");
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prefs.period_days, prefs.department, prefs.stale_days]);

    useEffect(() => {
        if (prefsReady) load();
    }, [prefsReady, load]);

    const persist = useCallback((nextPrefs, nextLayout) => {
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(() => {
            saveBoardPrefs({ ...nextPrefs, configured: true, widgets: nextLayout }).catch((e) => {
                showAlert(`Не удалось сохранить главную: ${e.message || e}`, "error");
            });
        }, 400);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => () => { if (saveTimer.current) clearTimeout(saveTimer.current); }, []);

    const changeLayout = (next) => { setLayout(next); persist(prefs, next); };
    const changePrefs = (patch) => { const next = { ...prefs, ...patch }; setPrefs(next); persist(next, layout); };
    const resetAll = () => changeLayout(mergeLayout([], false));
    const onAdd = (type) => { changeLayout(addModule(layout, type)); setEditing(true); };

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );
    const available = useMemo(() => availableTypes(user?.role, data?.meta?.forbidden), [user?.role, data?.meta?.forbidden]);
    const visible = layout.filter((w) => available.has(w.type));
    const onDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        changeLayout(reorderLayout(layout, active.id, over.id));
    };
    const ctx = { data, role: user?.role };
    const generated = data?.meta?.generated_at ? new Date(data.meta.generated_at).toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" }) : null;

    return (
        <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2 bg-white rounded-xl border border-slate-200/70 px-3 py-2 shadow-sm">
                <select aria-label="Период" value={prefs.period_days} onChange={(e) => changePrefs({ period_days: Number(e.target.value) })} className={selectCls}>
                    {PERIODS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
                <select aria-label="Отдел" value={prefs.department || ""} onChange={(e) => changePrefs({ department: e.target.value || null })} className={selectCls}>
                    <option value="">Все отделы</option>
                    {DEPARTMENTS.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
                <label className="flex items-center gap-1 text-sm text-slate-600" title="Через сколько дней без движения по кандидатам вакансия становится жёлтой">
                    Нет движения от
                    <input type="number" min={1} max={60} value={prefs.stale_days} onChange={(e) => changePrefs({ stale_days: Math.max(1, Math.min(60, Number(e.target.value) || 7)) })} className="w-14 border border-slate-200 rounded px-1 py-1 text-sm" />
                    дн.
                </label>
                <button type="button" onClick={load} className="px-2.5 py-1 rounded border border-slate-200 text-sm hover:bg-slate-50" disabled={loading}>{loading ? "Обновляю…" : "Обновить"}</button>
                {generated && <span className="text-xs text-slate-400">данные на {generated}</span>}
                <div className="ml-auto flex items-center gap-2">
                    {editing && <button type="button" onClick={resetAll} className="text-sm text-slate-500 hover:text-slate-900">Вернуть стартовый набор</button>}
                    <button type="button" onClick={() => setCatalog((v) => !v)} className="px-3 py-1 rounded text-sm font-medium border border-[#e0bb48] hover:bg-amber-50">+ Добавить модуль</button>
                    <button type="button" onClick={() => setEditing((v) => !v)} className={`px-3 py-1 rounded text-sm font-medium ${editing ? "bg-slate-900 text-white" : "bg-[#e0bb48] hover:bg-[#d4af3a] text-black"}`}>
                        {editing ? "Готово" : "Настроить"}
                    </button>
                </div>
            </div>

            {catalog && <ModuleCatalog available={available} layout={visible} onAdd={onAdd} onClose={() => setCatalog(false)} />}

            {editing && (
                <div className="bg-amber-50/60 border border-amber-200 rounded-xl px-3 py-2 text-sm text-slate-700">
                    Перетащите модуль за ⠿ или двигайте стрелками ↑↓; ширина — список в заголовке; ✕ — убрать с главной; ⚙ — настройки модуля. Всё сохраняется само, у каждого своя главная.
                </div>
            )}

            {!data && loading ? (
                <div className="py-16 text-center text-slate-400">Загрузка показателей…</div>
            ) : (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                    <SortableContext items={visible.map((w) => w.id)} strategy={rectSortingStrategy}>
                        <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
                            {visible.map((w, idx) => {
                                const def = WIDGET_BY_ID[w.type];
                                const Comp = WIDGET_COMPONENTS[w.type];
                                return (
                                    <ModuleCard
                                        key={w.id}
                                        widget={w}
                                        def={def}
                                        ctx={ctx}
                                        editing={editing}
                                        isFirst={idx === 0}
                                        isLast={idx === visible.length - 1}
                                        onPatch={(patch) => changeLayout(updateWidget(layout, w.id, patch))}
                                        onOption={(key, value) => changeLayout(setWidgetOption(layout, w.id, key, value))}
                                        onMove={(delta) => { const nb = visible[idx + delta]; if (nb) changeLayout(reorderLayout(layout, w.id, nb.id)); }}
                                        onRemove={() => changeLayout(removeModule(layout, w.id))}
                                    >
                                        {Comp ? <Comp data={data} options={w.options} tasks={tasks} tasksLoading={tasksLoading} role={user?.role} /> : null}
                                    </ModuleCard>
                                );
                            })}
                            {!visible.length && (
                                <div className="lg:col-span-4 py-10 text-center text-slate-400">
                                    На главной пока пусто — нажмите «+ Добавить модуль».
                                </div>
                            )}
                        </div>
                    </SortableContext>
                </DndContext>
            )}
        </div>
    );
}
